import * as enabledApi from '@shared/api/enabledPlayApi';
import { locales } from '@shared/constants/localization/LocalizationConstants';
import { DefaultClientListing } from '@shared/constants/organization/ProjectDefaults';
import { CustomControlsModel } from '@shared/models/devices/CustomControlsModel';
import DeviceTypeClientListingModel from '@shared/models/devices/DeviceTypeClientListingModel';
import DeviceTypeModel, { DeviceTypeFieldName } from '@shared/models/devices/DeviceTypeModel';
import UpdateDeviceTypeRequest from '@shared/models/devices/UpdateDeviceTypeRequest';
import OrganizationDeviceTypeModel from '@shared/models/organizations/OrganizationDeviceTypeModel';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
    ensureAllLocalesExistInClientListing,
    ensureAllLocalesExistInCustomControls,
    getDefaultDeviceType,
} from '../helpers/projectUtils';

export interface DeviceTypeEditorActions {
    updateOrgDeviceType: (
        organizationId: string,
        deviceId: string,
        request: UpdateDeviceTypeRequest
    ) => Promise<IResult<DeviceTypeModel>>;
    createOrgDeviceType: (
        organizationId: string,
        request: UpdateDeviceTypeRequest
    ) => Promise<IResult<DeviceTypeModel>>;
}

interface EditDeviceTypeState {
    // State
    editDeviceType: OrganizationDeviceTypeModel | null;
    editListing: DeviceTypeClientListingModel[] | null;
    editCustomControls: CustomControlsModel[] | null;
    hasChanges: boolean;
    listingHasChanges: boolean;
    customControlsHasChanges: boolean;
    loadingListing: boolean;
    loadingCustomControls: boolean;

    // Actions
    clearEditor: (organizationId?: string) => void;
    createUpdateRequest: () => UpdateDeviceTypeRequest | undefined;
    handleCreateAndClearEditor: (
        actions: DeviceTypeEditorActions,
        organizationId: string
    ) => Promise<IResult<DeviceTypeModel>>;
    handleFieldChange: <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => void;
    handleInputChange: <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => void;
    handleSave: (
        actions: DeviceTypeEditorActions,
        organizationId: string,
        deviceId?: string
    ) => Promise<IResult<DeviceTypeModel>>;
    initializeEditListing: (organizationId?: string, deviceId?: string) => Promise<void>;
    initializeEditCustomControls: (organizationId?: string, deviceId?: string) => Promise<void>;
    initializeEditor: (model: OrganizationDeviceTypeModel | null, organizationId?: string) => void;
    setEditDeviceType: (model: OrganizationDeviceTypeModel | null) => void;
    setEditListing: (listing: DeviceTypeClientListingModel[] | null) => void;
    setEditCustomControls: (controls: CustomControlsModel[] | null) => void;
    setListingHasChanges: (v: boolean) => void;
    setCustomControlsHasChanges: (v: boolean) => void;
}

export const useEditDeviceTypeStore = create<EditDeviceTypeState>()(
    immer((set, get) => {
        const setEditDeviceType = (model: OrganizationDeviceTypeModel | null) =>
            set((state) => {
                state.editDeviceType = model;
            });

        const setListingHasChanges = (v: boolean) =>
            set((state) => {
                state.listingHasChanges = v;
            });

        const setEditListing = (listing: DeviceTypeClientListingModel[] | null) =>
            set((state) => {
                state.editListing = listing;
            });

        const setCustomControlsHasChanges = (v: boolean) =>
            set((state) => {
                state.customControlsHasChanges = v;
            });

        const setEditCustomControls = (controls: CustomControlsModel[] | null) =>
            set((state) => {
                state.editCustomControls = controls;
            });

        const initializeEditCustomControls = async (organizationId?: string, deviceId?: string) => {
            set((state) => {
                state.loadingCustomControls = true;
            });
            let model: CustomControlsModel[] = [];
            if (organizationId && deviceId) {
                try {
                    const results = await Promise.all(
                        locales.voice.map((locale) => enabledApi.getDeviceTypeCustomControls(deviceId, locale))
                    );
                    const fetched: CustomControlsModel[] = [];
                    results.forEach((result, index) => {
                        if (result.resultType === 'Ok' && result.data) {
                            fetched.push({ ...result.data, locale: locales.voice[index] });
                        }
                    });
                    model = ensureAllLocalesExistInCustomControls(fetched, deviceId);
                } catch (error) {
                    console.error('Error fetching device type custom controls:', error);
                    model = ensureAllLocalesExistInCustomControls([], deviceId);
                }
            }
            set((state) => {
                state.editCustomControls = model;
                state.customControlsHasChanges = false;
                state.loadingCustomControls = false;
            });
        };

        const initializeEditListing = async (organizationId?: string, deviceId?: string) => {
            set((state) => {
                state.loadingListing = true;
            });
            let model: DeviceTypeClientListingModel[] = DefaultClientListing;
            if (organizationId && deviceId) {
                try {
                    const result = await enabledApi.getOrgDeviceTypeClientListings(organizationId, deviceId);
                    if (result.resultType === 'Ok' && result.data && result.data.length > 0) {
                        model = ensureAllLocalesExistInClientListing(result.data);
                    }
                    set((state) => {
                        state.listingHasChanges = false;
                    });
                } catch (error) {
                    console.error('Error fetching device type client listings:', error);
                } finally {
                    set((state) => {
                        state.editListing = model;
                        state.listingHasChanges = false;
                        state.loadingListing = false;
                    });
                }
            }
            set((state) => {
                state.editListing = model;
            });
        };

        const initializeEditor = (model: OrganizationDeviceTypeModel | null, organizationId?: string) => {
            set((state) => {
                if (model) {
                    state.editDeviceType = model;
                } else if (organizationId) {
                    const defaultType = getDefaultDeviceType(organizationId);
                    state.editDeviceType = defaultType;
                }
                state.hasChanges = false;
            });
        };

        const handleFieldChange = <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => {
            set((state) => {
                if (state.editDeviceType) {
                    state.editDeviceType = { ...state.editDeviceType, [fieldName]: value };
                }
                state.hasChanges = true;
            });
        };

        const handleInputChange: <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => void = (
            fieldName,
            value
        ) => {
            get().handleFieldChange(fieldName, value);
        };

        const createUpdateRequest = () => {
            const current = get().editDeviceType;
            if (current) return current as UpdateDeviceTypeRequest;
        };

        const handleSave = async (actions: DeviceTypeEditorActions, organizationId: string, deviceId?: string) => {
            if (get().editDeviceType?.id && deviceId) {
                return await handleUpdate(actions, organizationId, deviceId);
            } else {
                return await handleCreate(actions, organizationId);
            }
        };

        const handleCreateAndClearEditor = async (
            actions: DeviceTypeEditorActions,
            organizationId: string
        ): Promise<IResult<DeviceTypeModel>> => {
            const result = await handleCreate(actions, organizationId);
            if (result.resultType === 'Ok') {
                const defaultType = getDefaultDeviceType(organizationId);
                set((state) => {
                    state.editDeviceType = defaultType;
                    state.hasChanges = false;
                });
            }
            return result;
        };

        const clearEditor = (organizationId?: string) => {
            set((state) => {
                const model = organizationId ? getDefaultDeviceType(organizationId) : null;
                state.editDeviceType = model;
                state.hasChanges = false;
            });
        };

        return {
            // State
            editDeviceType: null,
            editListing: null,
            editCustomControls: null,
            hasChanges: false,
            listingHasChanges: false,
            customControlsHasChanges: false,
            loadingListing: false,
            loadingCustomControls: false,

            // Actions
            clearEditor,
            createUpdateRequest,
            handleCreateAndClearEditor,
            handleFieldChange,
            handleInputChange,
            handleSave,
            initializeEditListing,
            initializeEditCustomControls,
            initializeEditor,
            setEditDeviceType,
            setEditListing,
            setEditCustomControls,
            setListingHasChanges,
            setCustomControlsHasChanges,
        };
    })
);

async function handleUpdate(
    actions: DeviceTypeEditorActions,
    organizationId: string,
    deviceId: string
): Promise<IResult<DeviceTypeModel>> {
    try {
        const request = useEditDeviceTypeStore.getState().createUpdateRequest();
        if (!request) throw new Error('Missing request data');
        const result = await actions.updateOrgDeviceType(organizationId, deviceId, request);
        if (result.resultType === 'Ok' && result.data) {
            useEditDeviceTypeStore.getState().setEditDeviceType(result.data);
            useEditDeviceTypeStore.setState((state) => {
                state.hasChanges = false;
            });
        }
        return result;
    } catch {
        return new UnexpectedResult<DeviceTypeModel>();
    }
}

async function handleCreate(
    actions: DeviceTypeEditorActions,
    organizationId: string
): Promise<IResult<DeviceTypeModel>> {
    try {
        const request = useEditDeviceTypeStore.getState().createUpdateRequest();
        if (!request) throw new Error('Missing request data');
        const result = await actions.createOrgDeviceType(organizationId, request);
        if (result.resultType === 'Ok' && result.data) {
            useEditDeviceTypeStore.getState().setEditDeviceType(result.data);
            useEditDeviceTypeStore.setState((state) => {
                state.hasChanges = false;
            });
        }
        return result;
    } catch {
        return new UnexpectedResult<DeviceTypeModel>();
    }
}
